import React, { useState, useRef, useEffect } from 'react'
import pickerStyles from './FontPicker.module.scss'

// Lowercase letters and digits only, so "avenir next" finds "AvenirNextLTPro"
// and "times" finds "Times New Roman".
const normalize = (text) => (text || '').toLowerCase().replace(/[^a-z0-9]/g, '')

// Every word typed must appear somewhere in the font's name.
export function filterFontOptions(options, query) {
  const words = (query || '').split(/\s+/).map(normalize).filter(Boolean)
  if (words.length === 0) return options
  return options.filter((font) => {
    const name = normalize(font.label)
    return words.every((word) => name.includes(word))
  })
}

/**
 * FontPicker - the font button and its dropdown, shared by FloatingToolbar
 * (admin) and FixedToolbar (customer). The dropdown opens with a search box
 * focused: typing filters the list, arrow keys move through it, Enter picks,
 * Escape closes. The list still scrolls as before.
 *
 * `classes` is the host toolbar's style module, so the button and list keep
 * each toolbar's own look.
 */
export default function FontPicker({ options, currentFontName, onChange, classes, disabled = false, buttonLabel }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const rootRef = useRef(null)
  const listRef = useRef(null)
  const searchRef = useRef(null)
  // Set by the keyboard (and on open) so only it scrolls the list. Hover
  // also moves the highlight; scrolling for it would slide a half-visible row
  // out from under the mouse and onto the next one, and the list would drift.
  const scrollToHighlight = useRef(false)
  const listId = useRef(`font-picker-${Math.random().toString(36).slice(2)}`).current

  const matches = filterFontOptions(options, query)

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus()
  }, [open])

  // Keep the highlighted font in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current || !scrollToHighlight.current) return
    scrollToHighlight.current = false
    const item = listRef.current.querySelector(`[data-index="${highlight}"]`)
    if (item && item.scrollIntoView) item.scrollIntoView({ block: 'nearest' })
  }, [open, highlight])

  const openPicker = () => {
    const current = options.findIndex((font) => font.label === currentFontName)
    setQuery('')
    scrollToHighlight.current = true
    setHighlight(current >= 0 ? current : 0)
    setOpen(true)
  }

  const choose = (font) => {
    onChange(font.label, font.value)
    setOpen(false)
  }

  const handleQueryChange = (e) => {
    scrollToHighlight.current = true
    setQuery(e.target.value)
    setHighlight(0)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      scrollToHighlight.current = true
      setHighlight((i) => Math.min(i + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      scrollToHighlight.current = true
      setHighlight((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (matches[highlight]) choose(matches[highlight])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  return (
    <div className={classes.fontSelector} ref={rootRef}>
      <button
        type="button"
        className={classes.fontButton}
        onClick={() => !disabled && (open ? setOpen(false) : openPicker())}
        disabled={disabled}
      >
        <span className={classes.fontName}>{buttonLabel || currentFontName}</span>
        <span className={classes.dropdownArrow}>▼</span>
      </button>
      {open && !disabled && (
        <div className={`${classes.fontDropdown} ${pickerStyles.dropdown}`}>
          <input
            ref={searchRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={matches[highlight] ? `${listId}-${highlight}` : undefined}
            className={pickerStyles.search}
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            placeholder="Search fonts…"
            aria-label="Search fonts"
          />
          <div className={pickerStyles.list} ref={listRef} role="listbox" id={listId}>
            {matches.map((font, index) => (
              <div
                key={font.value}
                id={`${listId}-${index}`}
                data-index={index}
                role="option"
                aria-selected={font.label === currentFontName}
                className={`${classes.fontOption} ${index === highlight ? pickerStyles.highlighted : ''} ${font.label === currentFontName ? pickerStyles.current : ''}`}
                style={{ fontFamily: font.label }}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(font)}
              >
                {font.label}
              </div>
            ))}
            {matches.length === 0 && (
              <div className={pickerStyles.empty}>No fonts match “{query}”</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
